import React from "react";

import FormatCrypto from "@hl/base-components/lib/FormatCrypto";
import { WEIGHT_BOLD } from "@hl/base-components/lib/theme/typography";
import { User } from "@hl/shared-features/lib/features/auth/User";
import {
  getActivityType,
  StandardIcon,
} from "@hl/shared-features/lib/features/marketplace/activity/CollectionActivity";
import ActivityModal, {
  ActivityModalHeader,
} from "@hl/shared-features/lib/features/marketplace/modals/ActivityModal";
import { HighlightFields } from "@hl/shared-features/lib/features/marketplace/utils";
import { useModalStack } from "@hl/shared-features/lib/features/modal";
import {
  buildTxUrl,
  networkLookup,
} from "@hl/shared-features/lib/utils/blockExplorer";
import {
  Anchor,
  Center,
  Group,
  Loader,
  Stack,
  Text,
  UnstyledButton,
  useMantineTheme,
} from "@mantine/core";
import { useTokenActivity } from "@reservoir0x/reservoir-kit-ui";
import dayjs, { extend } from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";

import useMintState from "~hooks/useMintState";

import { SeriesToken } from "../../apollo/graphql.generated";

extend(relativeTime);

type Activity = ReturnType<typeof useTokenActivity>["data"][0] &
  HighlightFields;

const TokenDetailsSidebarHistory = ({ token }: { token: SeriesToken }) => {
  const { address, collection, chainId, marketplaceId, isImported } =
    useMintState();
  const { pushModal } = useModalStack();

  const theme = useMantineTheme();
  const {
    data: activities,
    isFetchingInitialData,
    error,
  } = useTokenActivity(`${address}:${token.tokenId}`, {
    limit: 6,
  });

  if (error) {
    return (
      <Center mt={24}>
        <Text color={theme.colors.secondaryText[0]} size="sm">
          Could not load the activities
        </Text>
      </Center>
    );
  }

  if (isFetchingInitialData) {
    return (
      <Center mt={24}>
        <Loader />
      </Center>
    );
  }

  return (
    <>
      {activities.slice(0, 12).map((activity, index) => (
        <Stack spacing={0} key={index}>
          <Group spacing="sm" py="xs" noWrap>
            {activity.txHash ? (
              <Anchor
                target="_blank"
                href={buildTxUrl(networkLookup(chainId).type, activity.txHash)}
                size="sm"
              >
                <StandardIcon icon={getActivityType(activity.type).icon} />
              </Anchor>
            ) : (
              <StandardIcon icon={getActivityType(activity.type).icon} />
            )}
            <Stack spacing={0}>
              <Text size="xs" color={theme.colors.secondaryText[0]}>
                {activity.timestamp && dayjs().to(activity.timestamp * 1000)}
              </Text>
              <Text
                display="flex"
                size="sm"
                sx={{ whiteSpace: "pre", alignItems: "center" }}
              >
                {getActivityText(activity as Activity)}
              </Text>
            </Stack>
          </Group>
        </Stack>
      ))}
      {activities.length > 5 && collection && (
        <UnstyledButton
          py="sm"
          mb="sm"
          onClick={() =>
            pushModal(
              <ActivityModal
                collectionData={{
                  id: collection.id,
                  onchainId: collection.onchainId,
                  address: collection.address || "",
                  chainId: collection.chainId,
                  name: collection.name || "",
                  marketplaceId: marketplaceId,
                  editionId: collection.editionId,
                }}
                isImported={isImported}
                tokenId={token.tokenId ?? undefined}
              />,
              {
                styles: { header: { marginBottom: 0 } },
                size: "100%",
                title: (
                  <ActivityModalHeader
                    tokenName={token?.name}
                    collectionName={collection?.name}
                  />
                ),
              }
            )
          }
        >
          <Text color={theme.colors.secondaryText[0]} size="sm">
            + View all activity
          </Text>
        </UnstyledButton>
      )}
      {!activities?.length && (
        <Text weight={WEIGHT_BOLD} size="sm" mt="sm">
          No history found
        </Text>
      )}
    </>
  );
};

export default TokenDetailsSidebarHistory;

const getActivityText = (activity: Activity) => {
  if (!activity) return;
  const {
    type,
    fromAddress,
    toAddress,
    price,
    highlightFromProfile,
    highlightToProfile,
  } = activity;
  const toName = highlightToProfile?.name;
  const fromName = highlightFromProfile?.name;

  switch (type) {
    case "sale":
      return (
        <>
          {userElement(toAddress, toName)} bought for {priceElement(price)}
        </>
      );
    case "ask":
      return (
        <>
          {userElement(fromAddress, fromName)} listed for {priceElement(price)}
        </>
      );
    case "transfer":
      return (
        <>
          Transferred from {userElement(fromAddress, fromName)} to{" "}
          {userElement(toAddress, toName)}
        </>
      );
    case "mint":
      return <>{userElement(toAddress, toName)} minted token</>;
    case "bid":
      return (
        <>
          {userElement(fromAddress, fromName)} offered {priceElement(price)}
        </>
      );
    case "bid_cancel":
      return <>{userElement(fromAddress, fromName)} cancelled their bid</>;
    case "ask_cancel":
      return <>{userElement(fromAddress, fromName)} cancelled their listing</>;
    default:
      return <>unknown event</>;
  }
};

const priceElement = (price: Activity["price"]) => {
  return (
    <FormatCrypto
      onlyText
      amount={price?.amount?.raw}
      symbol={price?.currency?.symbol}
    />
  );
};

const userElement = (address?: string, name?: string) => {
  return (
    <User
      bold
      enableLink
      small
      numLines="1"
      walletAddresses={[address ?? ""]}
      displayName={name}
    />
  );
};
